/**
 * React hooks for PeersCaller integration
 * Provides easy-to-use React hooks for video call functionality
 */

import { useEffect, useRef, useState, useCallback } from "react";
import { PeersCaller } from "../core/PeersCaller";
import { useCallStore } from "../store";
import type {
  PeersCallerConfig,
  PeersCallerCallbacks,
  CallParticipant,
  MediaStreamConfig,
  RecordingData,
} from "../types";

export interface UseVideoCallOptions extends PeersCallerConfig {
  callbacks?: PeersCallerCallbacks;
  autoInitialize?: boolean;
}

export interface UseVideoCallReturn {
  // Core methods
  initialize: () => Promise<void>;
  startCall: (mediaConfig?: MediaStreamConfig) => Promise<void>;
  joinCall: (mediaConfig?: MediaStreamConfig) => Promise<void>;
  endCall: () => void;

  // Media controls
  toggleAudio: (enabled: boolean) => void;
  toggleVideo: (enabled: boolean) => void;
  startScreenShare: () => Promise<void>;
  stopScreenShare: () => Promise<void>;

  // Recording
  startRecording: (recordingData: RecordingData) => Promise<void>;
  stopRecording: () => Promise<void>;

  // State
  callState: ReturnType<typeof useCallStore.getState>;
  participants: CallParticipant[];
  localParticipant: CallParticipant | null;
  isConnected: boolean;
  isRecording: boolean;
  error: string | null;

  // Utility
  cleanup: () => void;
  peersCaller: PeersCaller | null;
}

/**
 * Main hook for video call functionality
 */
export function useVideoCall(options: UseVideoCallOptions): UseVideoCallReturn {
  const [peersCaller, setPeersCaller] = useState<PeersCaller | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const initializeRef = useRef(false);

  // Store selectors
  const callState = useCallStore.getState();
  const participants = Object.values(
    useCallStore((state) => state.participants)
  );
  const localParticipant = useCallStore((state) => state.localParticipant);
  const isConnected = useCallStore((state) => state.callStatus === "connected");
  const isRecording = useCallStore((state) => state.isRecording);
  const error = useCallStore((state) => state.error);

  // Initialize PeersCaller
  const initialize = useCallback(async () => {
    if (initializeRef.current || peersCaller) return;

    try {
      initializeRef.current = true;

      const caller = new PeersCaller(options, options.callbacks);
      await caller.initialize();

      setPeersCaller(caller);
      setIsInitialized(true);
    } catch (error) {
      console.error("[useVideoCall] Failed to initialize:", error);
      initializeRef.current = false;
      throw error;
    }
  }, [options, peersCaller]);

  // Core call methods
  const startCall = useCallback(
    async (mediaConfig?: MediaStreamConfig) => {
      if (!peersCaller) {
        await initialize();
        return;
      }
      return peersCaller.startCall(mediaConfig);
    },
    [peersCaller, initialize]
  );

  const joinCall = useCallback(
    async (mediaConfig?: MediaStreamConfig) => {
      if (!peersCaller) {
        await initialize();
        return;
      }
      return peersCaller.joinCall(mediaConfig);
    },
    [peersCaller, initialize]
  );

  const endCall = useCallback(() => {
    peersCaller?.endCall();
  }, [peersCaller]);

  // Media control methods
  const toggleAudio = useCallback(
    (enabled: boolean) => {
      peersCaller?.toggleAudio(enabled);
    },
    [peersCaller]
  );

  const toggleVideo = useCallback(
    (enabled: boolean) => {
      peersCaller?.toggleVideo(enabled);
    },
    [peersCaller]
  );

  const startScreenShare = useCallback(async () => {
    if (!peersCaller) throw new Error("PeersCaller not initialized");
    return peersCaller.startScreenShare();
  }, [peersCaller]);

  const stopScreenShare = useCallback(async () => {
    if (!peersCaller) throw new Error("PeersCaller not initialized");
    return peersCaller.stopScreenShare();
  }, [peersCaller]);

  // Recording methods
  const startRecording = useCallback(
    async (recordingData: RecordingData) => {
      if (!peersCaller) throw new Error("PeersCaller not initialized");
      return peersCaller.startRecording(recordingData);
    },
    [peersCaller]
  );

  const stopRecording = useCallback(async () => {
    if (!peersCaller) throw new Error("PeersCaller not initialized");
    return peersCaller.stopRecording();
  }, [peersCaller]);

  // Cleanup
  const cleanup = useCallback(() => {
    if (peersCaller) {
      peersCaller.cleanup();
      setPeersCaller(null);
      setIsInitialized(false);
      initializeRef.current = false;
    }
  }, [peersCaller]);

  // Auto-initialize if enabled
  useEffect(() => {
    if (options.autoInitialize && !isInitialized && !initializeRef.current) {
      initialize().catch(console.error);
    }
  }, [options.autoInitialize, isInitialized, initialize]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  return {
    // Core methods
    initialize,
    startCall,
    joinCall,
    endCall,

    // Media controls
    toggleAudio,
    toggleVideo,
    startScreenShare,
    stopScreenShare,

    // Recording
    startRecording,
    stopRecording,

    // State
    callState,
    participants,
    localParticipant,
    isConnected,
    isRecording,
    error,

    // Utility
    cleanup,
    peersCaller,
  };
}

/**
 * Hook for accessing participant video streams
 */
export function useParticipantVideo(userId: string) {
  const [videoElement, setVideoElement] = useState<HTMLVideoElement | null>(
    null
  );
  const [stream, setStream] = useState<MediaStream | null>(null);

  const participant = useCallStore((state) => state.participants[userId]);

  useEffect(() => {
    if (participant?.stream) {
      setStream(participant.stream);

      // Create video element if it doesn't exist
      if (!videoElement) {
        const video = document.createElement("video");
        video.autoplay = true;
        video.playsInline = true;
        video.muted =
          userId === useCallStore.getState().localParticipant?.userId;
        setVideoElement(video);
      }

      // Attach stream to video element
      if (videoElement) {
        videoElement.srcObject = participant.stream;
      }
    }
  }, [participant?.stream, userId, videoElement]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (videoElement) {
        videoElement.srcObject = null;
      }
    };
  }, [videoElement]);

  return {
    videoElement,
    stream,
    participant,
  };
}

/**
 * Hook for call state management
 */
export function useCallState() {
  const state = useCallStore();

  return {
    conversationId: state.conversationId,
    participants: state.participants,
    localParticipant: state.localParticipant,
    isCalling: state.isCalling,
    isRecording: state.isRecording,
    recordLoading: state.recordLoading,
    callStatus: state.callStatus,
    error: state.error,

    // Computed values
    participantCount: Object.keys(state.participants).length,
    isConnected: state.callStatus === "connected",
    hasError: !!state.error,
  };
}

/**
 * Hook for media device management
 */
export function useMediaDevices() {
  const [devices, setDevices] = useState<{
    videoDevices: MediaDeviceInfo[];
    audioDevices: MediaDeviceInfo[];
  }>({ videoDevices: [], audioDevices: [] });

  const [permissions, setPermissions] = useState<{
    camera: PermissionState | null;
    microphone: PermissionState | null;
  }>({ camera: null, microphone: null });

  // Get available devices
  const getDevices = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      setDevices({
        videoDevices: devices.filter((device) => device.kind === "videoinput"),
        audioDevices: devices.filter((device) => device.kind === "audioinput"),
      });
    } catch (error) {
      console.error("[useMediaDevices] Failed to get devices:", error);
    }
  }, []);

  // Check permissions
  const checkPermissions = useCallback(async () => {
    try {
      if ("permissions" in navigator) {
        const cameraPermission = await navigator.permissions.query({
          name: "camera" as any,
        });
        const micPermission = await navigator.permissions.query({
          name: "microphone" as any,
        });

        setPermissions({
          camera: cameraPermission.state,
          microphone: micPermission.state,
        });
      }
    } catch (error) {
      console.error("[useMediaDevices] Failed to check permissions:", error);
    }
  }, []);

  // Request permissions
  const requestPermissions = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });
      stream.getTracks().forEach((track) => track.stop());

      await checkPermissions();
      await getDevices();

      return true;
    } catch (error) {
      console.error("[useMediaDevices] Failed to request permissions:", error);
      return false;
    }
  }, [checkPermissions, getDevices]);

  // Initialize on mount
  useEffect(() => {
    getDevices();
    checkPermissions();

    // Listen for device changes
    const handleDeviceChange = () => {
      getDevices();
    };

    navigator.mediaDevices?.addEventListener(
      "devicechange",
      handleDeviceChange
    );

    return () => {
      navigator.mediaDevices?.removeEventListener(
        "devicechange",
        handleDeviceChange
      );
    };
  }, [getDevices, checkPermissions]);

  return {
    devices,
    permissions,
    getDevices,
    checkPermissions,
    requestPermissions,
  };
}

/**
 * Hook for browser compatibility checking
 */
export function useBrowserSupport() {
  const [support, setSupport] = useState({
    webRTC: false,
    getUserMedia: false,
    getDisplayMedia: false,
    mediaRecorder: false,
    speechRecognition: false,
  });

  useEffect(() => {
    setSupport({
      webRTC: !!window.RTCPeerConnection,
      getUserMedia: !!(
        navigator.mediaDevices && navigator.mediaDevices.getUserMedia
      ),
      getDisplayMedia: !!(
        navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia
      ),
      mediaRecorder: typeof MediaRecorder !== "undefined",
      speechRecognition: !!(
        window.SpeechRecognition || (window as any).webkitSpeechRecognition
      ),
    });
  }, []);

  return {
    support,
    isSupported: Object.values(support).every(Boolean),
    unsupportedFeatures: Object.entries(support)
      .filter(([, supported]) => !supported)
      .map(([feature]) => feature),
  };
}
